import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { useLayoutEffect } from "react";
import {
  CustomFieldListOption,
  AdminCustomFieldsDocument,
  useUpsertCustomFieldListOptionMutation,
  AdminCustomFieldListOptionsDocument,
} from "@common/utils/graphql";
import { useToast } from "@/components/ui/use-toast";

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  value: z.string().min(1, "Value is required"),
});

type FormValues = z.infer<typeof FormSchema>;

type Props = {
  customFieldOption: CustomFieldListOption | null;
  onClose: () => void;
  onOpen: () => void;
  open: boolean;
  customFieldEntityId: number;
  customFieldEntityName: string;
};

export const CreateOrEditListOptionDialog = ({
  customFieldOption,
  onClose,
  onOpen,
  open,
  customFieldEntityId,
  customFieldEntityName,
}: Props) => {
  const isEditing = !!customFieldOption;
  const { toast } = useToast();
  const [upsertCustomFieldListOption] =
    useUpsertCustomFieldListOptionMutation();
  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
  });

  useLayoutEffect(() => {
    form.reset({
      name: customFieldOption?.name || "",
      value: customFieldOption?.value || "",
    });
  }, [customFieldOption, form, open]);

  const onCreateOrEdit = async (values: FormValues) => {
    try {
      const { data } = await upsertCustomFieldListOption({
        variables: {
          input: {
            uuid: customFieldOption?.uuid,
            name: values.name,
            value: values.value,
            customFieldEntityId,
          },
        },
        refetchQueries: [
          AdminCustomFieldsDocument,
          AdminCustomFieldListOptionsDocument,
        ],
      });
      if (
        data?.upsertCustomFieldListOption.__typename !=
        "MutationUpsertCustomFieldListOptionSuccess"
      ) {
        throw new Error();
      }
      onClose();
    } catch (e) {
      const title = isEditing
        ? `Failed to edit ${customFieldEntityName} option`
        : `Failed to create ${customFieldEntityName} option`;
      toast({
        title,
        description: "Please try again.",
        variant: "destructive",
      });
    }
    // save or edit
    // on success, refetch data and close dialog
    // on error, show toast with error message
  };

  return (
    <Dialog open={open}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          onClick={onOpen}
          size="lg"
          className="w-full"
        >
          Add New Option
        </Button>
      </DialogTrigger>
      <DialogContent
        onClose={onClose}
        className="gap-y-6 sm:min-w-96 sm:max-w-lg"
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? `Edit ${customFieldEntityName} Option`
              : `Create ${customFieldEntityName} Option`}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onCreateOrEdit)}
            className="w-full space-y-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Value" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button onClick={onClose} variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit">
                {isEditing ? "Save Changes" : "Create Option"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
